import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  Text,
  Title1,
  Title3,
  Button,
  Badge,
  Divider,
  makeStyles,
  tokens,
  Body1,
  Caption1,
  Spinner,
  Toast,
  ToastTitle,
  ToastBody,
  Toaster,
  useToastController,
  useId } from
'@fluentui/react-components';
import {
  CheckmarkCircle24Regular,
  Warning24Regular,
  ArrowSync24Regular,
  Checkmark24Regular,
  Dismiss24Regular } from
'@fluentui/react-icons';
import { useMSALAuth } from '../contexts/MSALAuthContext';
import { apiClient } from '../services/apiClient';

const useStyles = makeStyles({
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
    backgroundColor: tokens.colorNeutralBackground1,
    minHeight: '100vh'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    padding: '16px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: tokens.shadow4
  },
  card: {
    marginBottom: '16px',
    padding: '20px',
    boxShadow: tokens.shadow4
  },
  policyCard: {
    marginBottom: '12px',
    border: `2px solid ${tokens.colorNeutralStroke1}`
  },
  driftCard: {
    marginBottom: '12px',
    border: `2px solid ${tokens.colorPaletteYellowBorder1}`,
    backgroundColor: tokens.colorPaletteYellowBackground1
  },
  cardContent: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    marginTop: '16px'
  },
  section: {
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '6px'
  },
  domainList: {
    marginTop: '8px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px'
  },
  domain: {
    padding: '4px 8px',
    backgroundColor: 'white',
    borderRadius: '4px',
    fontSize: '12px',
    border: `1px solid ${tokens.colorNeutralStroke1}`
  },
  actionButtons: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px'
  },
  noDrift: {
    textAlign: 'center',
    padding: '60px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px'
  }
});

interface DriftPolicy {
  policyName: string;
  hasDrift: boolean;
  status: 'added' | 'modified' | 'deleted';
  teamsData?: {
    allowedDomains: string[];
    blockedDomains: string[];
  };
  dbData?: {
    allowedDomains: string[];
    blockedDomains: string[];
  };
  changes: string[];
}

export const SimpleDriftPage: React.FC = () => {
  const styles = useStyles();
  const { isAuthenticated, isAuthReady } = useMSALAuth();
  const [driftPolicies, setDriftPolicies] = useState<DriftPolicy[]>([]);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [syncingAll, setSyncingAll] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const toasterId = useId("toaster");
  const { dispatchToast } = useToastController(toasterId);

  // Helper function to remove a policy from the drift list
  const removePolicyFromList = (policyName: string) => {
    setDriftPolicies((prev) => prev.filter((p) => p.policyName !== policyName));
  };

  // Helper function to remove multiple policies from the drift list
  const removePoliciesFromList = (policyNames: string[]) => {
    setDriftPolicies((prev) => prev.filter((p) => !policyNames.includes(p.policyName)));
  };

  // Helper function to check if error is non-transient (should not retry)
  const isNonTransientError = (error: Error): boolean => {
    return error.message.includes('validation') || error.message.includes('not found');
  };

  // Helper function to sync a single policy with retry logic
  const syncPolicyWithRetry = async (policy: DriftPolicy, MAX_RETRIES: number, RETRY_DELAY_MS: number): Promise<{success: boolean;error?: string;}> => {
    let success = false;
    let lastError: Error | null = null;

    // Try up to MAX_RETRIES times
    for (let attempt = 1; attempt <= MAX_RETRIES && !success; attempt++) {
      try {
        if (attempt > 1) {
          console.log(`🔄 Retry attempt ${attempt} for: ${policy.policyName}`);
          // Wait before retrying
          await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
        } else {
          console.log(`📡 Syncing: ${policy.policyName}`);
        }

        await apiClient.post('/unified-drift/sync-policy', { policyName: policy.policyName });

        console.log(`✅ Synced: ${policy.policyName}`);
        success = true;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error('Failed to sync');
        console.error(`❌ Attempt ${attempt} failed for ${policy.policyName}:`, err);

        // Don't retry if it's a validation error (non-transient)
        if (isNonTransientError(lastError)) {
          console.log(`⚠️  Non-transient error, skipping retries for ${policy.policyName}`);
          break;
        }
      }
    }

    return { success, error: lastError?.message };
  };

  // Helper function to show sync results toast notifications
  const showSyncResults = (results: {success: string[];failed: {name: string;error: string;}[];}, totalCount: number) => {
    // Show results
    if (results.success.length > 0) {
      dispatchToast(
        <Toast data-testid="simple-drift-page-sync-results-toast">
          <ToastTitle data-testid="simple-drift-page-sync-complete-toast-title">Sync Complete</ToastTitle>
          <ToastBody data-testid="simple-drift-page-sync-complete-toast-body">
            Successfully synced {results.success.length} of {totalCount} policies
            {results.failed.length > 0 && `. Failed: ${results.failed.map((f) => f.name).join(', ')}`}
          </ToastBody>
        </Toast>,
        { intent: results.failed.length === 0 ? 'success' : 'warning' }
      );
    }

    if (results.failed.length > 0 && results.success.length === 0) {
      dispatchToast(
        <Toast data-testid="simple-drift-page-sync-all-failed-toast">
          <ToastTitle data-testid="simple-drift-page-sync-all-failed-toast-title">Sync Failed</ToastTitle>
          <ToastBody data-testid="simple-drift-page-sync-all-failed-toast-body">All {results.failed.length} policies failed to sync. Please try again.</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    }
  };

  useEffect(() => {
    if (isAuthenticated && isAuthReady) {
      fetchDriftData();
    }
  }, [isAuthenticated, isAuthReady]); // Re-fetch when authentication is ready

  const fetchDriftData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/policies/drift-notifications');
      const data = response as any;

      // CRITICAL FIX: Handle both empty notifications AND successful response with notifications
      // Don't treat empty notifications as an error - it means everything is in sync!
      if (data.success) {
        if (data.notifications && data.notifications.length > 0) {
          // Transform the API response to match our interface
          const transformedData: DriftPolicy[] = data.notifications.map((notif: any) => {
            console.log('Processing notification:', notif);

            // Extract domains from the notification structure
            const teamsAllowed = notif.teamsData?.allowedDomains || [];
            const teamsBlocked = notif.teamsData?.blockedDomains || [];
            const dbAllowed = notif.dbData?.allowedDomains || [];
            const dbBlocked = notif.dbData?.blockedDomains || [];

            console.log(`Policy ${notif.policyName}:`, {
              teamsAllowed,
              teamsBlocked,
              dbAllowed,
              dbBlocked
            });

            return {
              policyName: notif.policyName,
              hasDrift: true,
              status: notif.driftType || 'modified',
              teamsData: {
                allowedDomains: teamsAllowed,
                blockedDomains: teamsBlocked
              },
              dbData: {
                allowedDomains: dbAllowed,
                blockedDomains: dbBlocked
              },
              changes: notif.changes || [notif.message || 'Drift detected']
            };
          });

          setDriftPolicies(transformedData);
        } else {
          // No notifications means everything is in sync - this is NOT an error!
          setDriftPolicies([]);
        }
      } else {
        // Only throw error if the API response indicates failure
        throw new Error(data.error || 'Failed to fetch drift notifications');
      }
    } catch (err) {
      console.error('Error fetching drift data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch drift notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (policyName: string) => {
    console.log('🔄 Starting sync for policy:', policyName);
    setSyncing(policyName);

    try {
      console.log('📡 Sending sync request for:', policyName);
      const response = await apiClient.post('/unified-drift/sync-policy', { policyName });

      console.log('📨 Response data:', response);

      // Show success toast
      dispatchToast(
        <Toast data-testid="simple-drift-page-sync-policy-success-toast">
          <ToastTitle data-testid="simple-drift-page-sync-policy-success-toast-title">Sync Successful</ToastTitle>
          <ToastBody data-testid="simple-drift-page-sync-policy-success-toast-body">Policy "{policyName}" has been synchronized with Teams configuration</ToastBody>
        </Toast>,
        { intent: 'success' }
      );

      // Remove the synced policy from the list with a slight delay for visual feedback
      setTimeout(() => {
        removePolicyFromList(policyName);
      }, 500);

      console.log('✅ Sync successful, policy removed from drift list');
    } catch (err) {
      console.error('❌ Error syncing policy:', err);

      // Show error toast
      dispatchToast(
        <Toast data-testid="simple-drift-page-sync-policy-failed-toast">
          <ToastTitle data-testid="simple-drift-page-sync-policy-failed-toast-title">Sync Failed</ToastTitle>
          <ToastBody data-testid="simple-drift-page-sync-policy-failed-toast-body">{err instanceof Error ? err.message : 'Failed to sync policy. Please try again.'}</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    } finally {
      setSyncing(null);
    }
  };

  const handleRevert = async (policyName: string) => {
    console.log('🔄 Reverting policy to database state:', policyName);
    setSyncing(policyName);

    try {
      console.log('📡 Sending revert request for:', policyName);
      const response = await apiClient.post('/unified-drift/revert-policy', { policyName });

      console.log('📨 Revert response:', response);

      // Show success toast
      dispatchToast(
        <Toast data-testid="simple-drift-page-revert-policy-success-toast">
          <ToastTitle data-testid="simple-drift-page-revert-policy-success-toast-title">Revert Successful</ToastTitle>
          <ToastBody data-testid="simple-drift-page-revert-policy-success-toast-body">Policy "{policyName}" has been reverted to database configuration</ToastBody>
        </Toast>,
        { intent: 'success' }
      );

      // Remove from drift list since it's now synchronized
      removePolicyFromList(policyName);
    } catch (err: any) {
      console.error('❌ Failed to revert policy:', err);
      dispatchToast(
        <Toast data-testid="simple-drift-page-revert-policy-failed-toast">
          <ToastTitle data-testid="simple-drift-page-revert-policy-failed-toast-title">Revert Failed</ToastTitle>
          <ToastBody data-testid="simple-drift-page-revert-policy-failed-toast-body">{err?.message || 'Failed to revert policy'}</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    } finally {
      setSyncing(null);
    }
  };

  const handleRefresh = () => {
    fetchDriftData();
  };

  const getPolicyName = (p: DriftPolicy) => `• ${p.policyName}`;

  const handleSyncAll = async () => {
    if (driftPolicies.length === 0) return;

    const confirmMessage = `Are you sure you want to sync all ${driftPolicies.length} policies?\n\nThis will update the database with the current Teams configuration for:\n${driftPolicies.map(getPolicyName).join('\n')}`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    console.log('🔄 Starting sync for all policies:', driftPolicies.map(getPolicyName));
    setSyncingAll(true);

    const results = {
      success: [] as string[],
      failed: [] as {name: string;error: string;}[]
    };

    try {
      // Sync all policies sequentially with retry logic
      const MAX_RETRIES = 2;
      const RETRY_DELAY_MS = 2000; // 2 seconds between retries

      for (const policy of driftPolicies) {
        const syncResult = await syncPolicyWithRetry(policy, MAX_RETRIES, RETRY_DELAY_MS);

        if (syncResult.success) {
          results.success.push(policy.policyName);
        } else if (syncResult.error) {
          // If all retries failed, record the failure
          results.failed.push({ name: policy.policyName, error: syncResult.error });
        }
      }

      showSyncResults(results, driftPolicies.length);

      // Remove successfully synced policies from the list
      if (results.success.length > 0) {
        setTimeout(() => {
          removePoliciesFromList(results.success);
        }, 500);
      }

    } catch (err) {
      console.error('❌ Error in sync all:', err);
      dispatchToast(
        <Toast data-testid="simple-drift-page-sync-all-error-toast">
          <ToastTitle data-testid="simple-drift-page-sync-all-error-toast-title">Sync Failed</ToastTitle>
          <ToastBody data-testid="simple-drift-page-sync-all-error-toast-body">{err instanceof Error ? err.message : 'Failed to sync policies. Please try again.'}</ToastBody>
        </Toast>,
        { intent: 'error' }
      );
    } finally {
      setSyncingAll(false);
    }
  };

  const renderDomains = (domains: string[], label: string, highlightDiff?: boolean, otherDomains?: string[]) => {
    // If we should highlight differences and have something to compare to
    const getDomainStyle = (domain: string) => {
      if (!highlightDiff || !otherDomains) return {};

      // Check if this domain is missing from the other list
      if (!otherDomains.includes(domain)) {
        return {
          backgroundColor: '#ffe4e1',
          border: '1px solid #ff6b6b',
          fontWeight: 'bold'
        };
      }
      return {};
    };

    return (
      <div className={styles.section} data-testid="simple-drift-page-domains-section">
        <Text weight="semibold" size={200} data-testid="simple-drift-page-domains-section-label">{label}</Text>
        {domains.length === 0 ?
        <Text size={200} italic style={{ marginLeft: '6px' }} data-testid="simple-drift-page-no-domains-configured-text">No domains configured</Text> :

        <div className={styles.domainList} data-testid="simple-drift-page-domain-list">
            {domains.map((domain, idx) =>
          <span
            key={idx}
            className={styles.domain}
            style={getDomainStyle(domain)}
            title={highlightDiff && !otherDomains?.includes(domain) ? 'This domain differs' : ''} data-testid="simple-drift-page-domain-chip">

                {domain}
              </span>
          )}
          </div>
        }
      </div>);

  };

  return (
    <div className={styles.container} data-testid="simple-drift-page-container">
      <Toaster toasterId={toasterId} data-testid="simple-drift-page-toaster" />
      {/* Header */}
      <div className={styles.header} data-testid="simple-drift-page-header">
        <div data-testid="simple-drift-page-header-text">
          <Title1 data-testid="simple-drift-page-title">Drift Notifications</Title1>
          <Text style={{ marginTop: '8px', display: 'block' }} data-testid="simple-drift-page-description">Review and resolve policy differences between Teams Admin Center and ADM</Text>
        </div>
        <div style={{ display: 'flex', gap: '8px' }} data-testid="simple-drift-page-header-actions">
          {driftPolicies.length > 0 &&
          <Button
            appearance="primary"
            icon={<Checkmark24Regular data-testid="simple-drift-page-sync-all-icon" />}
            onClick={handleSyncAll}
            disabled={loading || syncingAll} data-testid="simple-drift-page-sync-all-button">

              {syncingAll ? `Syncing ${driftPolicies.length} policies...` : `Sync All (${driftPolicies.length})`}
            </Button>
          }
          <Button
            appearance={driftPolicies.length > 0 ? "secondary" : "primary"}
            icon={<ArrowSync24Regular data-testid="simple-drift-page-refresh-icon" />}
            onClick={handleRefresh}
            disabled={loading || syncingAll} data-testid="simple-drift-page-refresh-button">

            Refresh
          </Button>
        </div>
      </div>

      {/* Loading State */}
      {loading &&
      <Card className={styles.card} data-testid="simple-drift-page-loading-card">
          <div style={{ textAlign: 'center', padding: '40px' }} data-testid="simple-drift-page-loading-state">
            <Spinner size="large" label="Checking for drift..." data-testid="simple-drift-page-loading-spinner" />
          </div>
        </Card>
      }

      {/* Error State */}
      {error && !loading &&
      <Card className={styles.card} data-testid="simple-drift-page-error-card">
          <div style={{ textAlign: 'center', padding: '20px' }} data-testid="simple-drift-page-error-state">
            <Warning24Regular fontSize={48} color={tokens.colorPaletteRedForeground1} data-testid="simple-drift-page-error-icon" />
            <Title3 block style={{ marginTop: '16px', color: tokens.colorPaletteRedForeground1 }} data-testid="simple-drift-page-error-title">
              Error Loading Drift Status
            </Title3>
            <Text data-testid="simple-drift-page-error-text">{error}</Text>
          </div>
        </Card>
      }

      {/* No Drift State - CRITICAL: This is the NORMAL state when everything is in sync */}
      {!loading && !error && driftPolicies.length === 0 &&
      <Card className={styles.card} data-testid="simple-drift-page-no-drift-card">
          <div className={styles.noDrift} data-testid="simple-drift-page-no-drift-state">
            <CheckmarkCircle24Regular
            fontSize={64}
            color={tokens.colorPaletteGreenForeground1} data-testid="simple-drift-page-no-drift-icon" />

            <Title3 block style={{ marginTop: '16px' }} data-testid="simple-drift-page-no-drift-title">
              Everything is in Sync
            </Title3>
            <Text data-testid="simple-drift-page-no-drift-description">No drift detected. Your Teams policies match the database perfectly.</Text>
            <Caption1 style={{ marginTop: '8px' }} data-testid="simple-drift-page-no-drift-caption">
              This page will automatically show any policies that need attention when drift is detected.
            </Caption1>
          </div>
        </Card>
      }

      {/* Drift Cards */}
      {!loading && !error && driftPolicies.map((policy, index) =>
      <Card key={policy.policyName} className={styles.driftCard} data-testid={`simple-drift-page-card-${index}`}>
          <CardHeader
          header={
          <div className={styles.statusRow} data-testid={`simple-drift-page-policy-status-row-${index}`}>
                <Warning24Regular color={tokens.colorPaletteYellowForeground1} data-testid={`simple-drift-page-policy-warning-icon-${index}`} />
                <Text weight="semibold" size={400} data-testid={`simple-drift-page-policy-name-${index}`}>{policy.policyName}</Text>
                <Badge appearance="filled" color="warning" data-testid={`simple-drift-page-policy-drift-badge-${index}`}>Drift Detected</Badge>
              </div>
          }
          description={
          <div data-testid={`simple-drift-page-policy-change-list-${index}`}>
                {policy.changes.map((change, idx) =>
            <Text key={idx} size={200} block data-testid={`simple-drift-page-policy-change-${index}-${idx}`}>• {change}</Text>
            )}
              </div>
          } data-testid={`simple-drift-page-policy-card-header-${index}`} />


          <Divider data-testid={`simple-drift-page-policy-card-divider-${index}`} />

          <div className={styles.cardContent} data-testid={`simple-drift-page-policy-card-content-${index}`}>
            {/* Teams State */}
            <div data-testid={`simple-drift-page-teams-state-column-${index}`}>
              <Text weight="semibold" data-testid={`simple-drift-page-teams-state-title-${index}`}>Teams Admin Center (Current State)</Text>
              {renderDomains(
              policy.teamsData?.allowedDomains || [],
              'Allowed Domains',
              true,
              policy.dbData?.allowedDomains
            )}
              {renderDomains(
              policy.teamsData?.blockedDomains || [],
              'Blocked Domains',
              true,
              policy.dbData?.blockedDomains
            )}
            </div>

            {/* Database State */}
            <div data-testid={`simple-drift-page-adm-state-column-${index}`}>
              <Text weight="semibold" data-testid={`simple-drift-page-adm-state-title-${index}`}>ADM (Out of Sync)</Text>
              {renderDomains(
              policy.dbData?.allowedDomains || [],
              'Allowed Domains',
              true,
              policy.teamsData?.allowedDomains
            )}
              {renderDomains(
              policy.dbData?.blockedDomains || [],
              'Blocked Domains',
              true,
              policy.teamsData?.blockedDomains
            )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className={styles.actionButtons} data-testid={`simple-drift-page-policy-action-buttons-${index}`}>
            <Button
            appearance="primary"
            icon={<Checkmark24Regular data-testid={`simple-drift-page-sync-policy-icon-${index}`} />}
            onClick={() => handleSync(policy.policyName)}
            disabled={syncing === policy.policyName || syncingAll} data-testid={`simple-drift-page-sync-policy-button-${index}`}>

              {syncing === policy.policyName ? 'Syncing...' : 'Sync Policy'}
            </Button>
            <Button
            appearance="secondary"
            icon={<Dismiss24Regular data-testid={`simple-drift-page-revert-policy-icon-${index}`} />}
            onClick={() => handleRevert(policy.policyName)}
            disabled={syncing === policy.policyName || syncingAll} data-testid={`simple-drift-page-revert-policy-button-${index}`}>

              {syncing === policy.policyName ? 'Reverting...' : 'Revert Changes'}
            </Button>
          </div>
        </Card>
      )}
    </div>);

};